import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Loader2,
  AlertTriangle,
  Sparkles,
  CheckCircle2,
  XCircle,
  Trophy,
  Play,
  LogIn,
  UserPlus,
  ArrowLeft,
  ArrowRight,
  Send,
  RotateCcw,
  ListChecks,
  Clock3,
  ShieldCheck,
} from 'lucide-react';
import logo from '../../img/logo.png';
import TheoryRenderer from '../../components/course/TheoryRenderer';
import {
  useGetPublicQuizQuery,
  useStartPublicQuizMutation,
  useSubmitPublicQuizMutation,
} from '../../api/apiSlice';

// Public quiz page flow: intro → play → result

const PublicQuizPage = () => {
  const { slug } = useParams();
  const { data, isLoading, error, refetch } = useGetPublicQuizQuery(slug);
  const [startMut, { isLoading: starting }] = useStartPublicQuizMutation();
  const [submitMut, { isLoading: submitting }] = useSubmitPublicQuizMutation();

  const [stage, setStage] = useState('intro');
  const [name, setName] = useState('');
  const [attemptId, setAttemptId] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [result, setResult] = useState(null);

  const start = async () => {
    const participantName = name.trim();
    if (!participantName) return;

    try {
      const res = await startMut({ slug, participantName }).unwrap();
      setAttemptId(res.attemptId);
      setQuiz(res.quiz);
      setAnswers({});
      setResult(null);
      setCurrentIdx(0);
      setStage('play');
    } catch (err) {
      alert(err?.data?.message || 'Не удалось начать квиз');
    }
  };

  const finish = async () => {
    try {
      const payload = (quiz?.questions || []).map((q) => ({
        questionId: q._id,
        selectedOptionIndex: answers[q._id] ?? null,
      }));

      const res = await submitMut({ slug, attemptId, answers: payload }).unwrap();
      setResult(res);
      setStage('result');
    } catch (err) {
      alert(err?.data?.message || 'Не удалось отправить ответы');
    }
  };

  const restart = () => {
    setStage('intro');
    setAttemptId(null);
    setQuiz(null);
    setAnswers({});
    setCurrentIdx(0);
    setResult(null);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-white">
      <BackgroundDecor />

      <header className="sticky top-0 z-30 border-b border-white/50 bg-white/75 backdrop-blur-2xl dark:border-white/10 dark:bg-slate-950/70">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:h-20 sm:px-6 lg:px-8">
          <Link to="/" className="group flex items-center gap-3" aria-label="Uaqyt">
               <img 
              src={logo} 
              alt="Uaqyt" 
              className="h-[150px] w-150px]" // Нақты 150px өлшемі
            />
          </Link>

          <div className="flex items-center gap-2">
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-200 hover:text-indigo-700 hover:shadow-md dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:text-white sm:px-4"
            >
              <LogIn className="h-4 w-4" />
              <span className="hidden sm:inline">Войти</span>
            </Link>

            <Link
              to="/auth"
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-indigo-500/25 sm:px-5"
            >
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">Регистрация</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <div className="mx-auto max-w-4xl">
          {isLoading && <LoadingState />}

          {error && (
            <ErrorCard
              title="Квиз не найден"
              message={error?.data?.message || 'Проверьте корректность ссылки'}
              onRetry={refetch}
            />
          )}

          {data && !data.available && (
            <ErrorCard
              title="Квиз больше недоступен"
              message="Срок действия истёк. Свяжитесь с автором квиза, если нужна свежая ссылка."
              showCta
            />
          )}

          {data?.available && stage === 'intro' && (
            <IntroCard
              quiz={data.quiz}
              name={name}
              setName={setName}
              onStart={start}
              starting={starting}
            />
          )}

          {data?.available && stage === 'play' && quiz && (
            <PlayCard
              quiz={quiz}
              answers={answers}
              setAnswers={setAnswers}
              currentIdx={currentIdx}
              setCurrentIdx={setCurrentIdx}
              onFinish={finish}
              submitting={submitting}
            />
          )}

          {stage === 'result' && result && (
            <ResultCard
              result={result}
              quiz={quiz}
              participantName={name}
              onRestart={restart}
            />
          )}
        </div>
      </main>

      <footer className="relative z-10 border-t border-slate-200/70 bg-white/60 px-4 py-6 text-center text-xs text-slate-500 backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400 sm:px-6">
        Создано на платформе Uaqyt ·{' '}
        <Link to="/" className="font-semibold text-indigo-600 hover:underline dark:text-indigo-400">
          Узнать больше
        </Link>
      </footer>
    </div>
  );
};

const BackgroundDecor = () => (
  <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
    <div className="absolute left-1/2 top-[-260px] h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-indigo-200/50 blur-3xl dark:bg-indigo-900/25" />
    <div className="absolute -left-40 top-1/4 h-[420px] w-[420px] rounded-full bg-violet-200/50 blur-3xl dark:bg-violet-900/20" />
    <div className="absolute -right-40 bottom-10 h-[460px] w-[460px] rounded-full bg-sky-200/50 blur-3xl dark:bg-sky-900/20" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(99,102,241,0.14)_1px,transparent_0)] [background-size:28px_28px] opacity-40 dark:opacity-20" />
  </div>
);

const LoadingState = () => (
  <div className="flex min-h-[420px] items-center justify-center">
    <div className="rounded-3xl border border-slate-200 bg-white/80 p-8 text-center shadow-xl shadow-slate-200/60 backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20">
      <Loader2 className="mx-auto h-10 w-10 animate-spin text-indigo-600" />
      <p className="mt-4 text-sm font-medium text-slate-500 dark:text-slate-400">Квиз загружается…</p>
    </div>
  </div>
);

const ErrorCard = ({ title, message, onRetry, showCta }) => (
  <div className="mx-auto max-w-xl rounded-[2rem] border border-slate-200 bg-white/85 p-7 text-center shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20 sm:p-9">
    <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-3xl bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
      <AlertTriangle className="h-8 w-8" />
    </div>
    <h2 className="text-2xl font-bold tracking-tight text-slate-950 dark:text-white">{title}</h2>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600 dark:text-slate-400">{message}</p>

    <div className="mt-7 flex justify-center">
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-500/20 transition hover:-translate-y-0.5 hover:bg-indigo-700"
        >
          <RotateCcw className="h-4 w-4" />
          Попробовать снова
        </button>
      )}

      {showCta && (
        <Link
          to="/courses"
          className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-500/20 transition hover:-translate-y-0.5 hover:bg-indigo-700"
        >
          Посмотреть курсы Uaqyt
        </Link>
      )}
    </div>
  </div>
);

const IntroCard = ({ quiz, name, setName, onStart, starting }) => {
  const canStart = Boolean(name.trim()) && !starting;

  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white/85 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20">
      <div className="relative min-h-[240px] overflow-hidden bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 sm:min-h-[300px]">
        {quiz.coverImageWebpUrl && (
          <img
            src={quiz.coverImageWebpUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/25 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.22),transparent_30%),radial-gradient(circle_at_80%_10%,rgba(255,255,255,0.18),transparent_26%)]" />

        <div className="absolute left-5 top-5 flex flex-wrap items-center gap-2">
          <Badge icon={Sparkles}>Публичный квиз</Badge>
          <Badge icon={ListChecks}>{quiz.totalQuestions} вопросов</Badge>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8">
          <h1 className="max-w-3xl text-3xl font-black tracking-tight text-white drop-shadow-sm sm:text-5xl">
            {quiz.title}
          </h1>
          {quiz.description && (
            <p className="mt-3 max-w-2xl whitespace-pre-line break-words text-sm leading-6 text-white/85 [overflow-wrap:anywhere] sm:text-base">
              {quiz.description}
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-6 p-5 sm:p-8 lg:grid-cols-[1fr_360px] lg:p-10">
        <div className="space-y-4">
          <div className="rounded-3xl border border-indigo-100 bg-indigo-50/70 p-5 dark:border-indigo-500/20 dark:bg-indigo-500/10">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-indigo-600 shadow-sm dark:bg-white/10 dark:text-indigo-300">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-bold text-slate-950 dark:text-white">Быстрый старт без аккаунта</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Введите имя, ответьте на вопросы и сразу получите результат. Автор увидит ваш результат в статистике.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <MiniInfo icon={Clock3} title="Займёт пару минут" text="Проходите в удобном темпе." />
            <MiniInfo icon={Trophy} title="Результат сразу" text="Баллы и ответы появятся после отправки." />
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-slate-950/40 dark:shadow-black/10">
          <label className="block text-sm font-bold text-slate-800 dark:text-slate-200">
            Как вас зовут? <span className="text-indigo-600">*</span>
          </label>

          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            placeholder="Например: Айдар"
            onKeyDown={(e) => e.key === 'Enter' && canStart && onStart()}
            className="mt-3 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:border-indigo-400 dark:focus:ring-indigo-500/20"
          />

          <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
            Аккаунт не нужен. Имя используется только для результата и лидерборда автора.
          </p>

          <button
            type="button"
            onClick={onStart}
            disabled={!canStart}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-4 text-base font-black text-white shadow-xl shadow-indigo-500/25 transition hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-indigo-500/30 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />}
            {starting ? 'Начинаем…' : 'Начать квиз'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PlayCard = ({ quiz, answers, setAnswers, currentIdx, setCurrentIdx, onFinish, submitting }) => {
  const total = quiz.questions.length;
  const q = quiz.questions[currentIdx];
  const isLast = currentIdx === total - 1;
  const selected = answers[q._id];
  const answeredCount = Object.keys(answers).length;
  const progress = Math.round(((currentIdx + 1) / total) * 100);

  const choose = (k) => setAnswers((prev) => ({ ...prev, [q._id]: k }));

  return (
    <div className="rounded-[2rem] border border-slate-200 bg-white/90 p-5 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20 sm:p-8">
      <div className="mb-7 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.03]">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm">
          <div>
            <p className="font-black text-slate-950 dark:text-white">
              Вопрос {currentIdx + 1} из {total}
            </p>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Отвечено: {answeredCount}/{total}
            </p>
          </div>
          <div className="rounded-2xl bg-white px-3 py-1.5 text-xs font-black text-indigo-700 shadow-sm dark:bg-white/10 dark:text-indigo-300">
            {progress}%
          </div>
        </div>

        <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
          <div
            className="h-full rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="mb-5 rounded-3xl bg-gradient-to-br from-slate-900 to-indigo-950 p-5 text-white shadow-xl shadow-indigo-950/10 sm:p-7">
        <div className="text-xl font-black leading-8 tracking-tight sm:text-2xl [&_p]:m-0">
          <TheoryRenderer content={q.text || ''} />
        </div>
      </div>

      <div className="space-y-3">
        {q.options.map((option, index) => {
          const active = selected === index;
          return (
            <button
              key={option._id || index}
              type="button"
              onClick={() => choose(index)}
              className={`group flex w-full items-start gap-3 rounded-3xl border p-4 text-left transition hover:-translate-y-0.5 sm:p-5 ${
                active
                  ? 'border-indigo-400 bg-indigo-50 shadow-lg shadow-indigo-500/10 dark:border-indigo-400/60 dark:bg-indigo-500/15'
                  : 'border-slate-200 bg-white hover:border-indigo-200 hover:bg-slate-50 hover:shadow-md dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]'
              }`}
            >
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl text-sm font-black transition ${
                  active
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-700 dark:bg-white/10 dark:text-slate-300'
                }`}
              >
                {String.fromCharCode(65 + index)}
              </span>
              <span className="min-w-0 flex-1 text-sm font-semibold text-slate-800 dark:text-slate-100 sm:text-base [&_p]:m-0">
                <TheoryRenderer content={option.text || ''} />
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-7 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
          disabled={currentIdx === 0}
          className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
        >
          <ArrowLeft className="h-4 w-4" />
          Назад
        </button>

        {isLast ? (
          <button
            type="button"
            onClick={onFinish}
            disabled={selected === undefined || submitting}
            className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-emerald-500/20 transition hover:-translate-y-0.5 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {submitting ? 'Отправка…' : 'Завершить'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setCurrentIdx((i) => Math.min(total - 1, i + 1))}
            disabled={selected === undefined}
            className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-indigo-500/20 transition hover:-translate-y-0.5 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
          >
            Далее
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const ResultCard = ({ result, quiz, participantName, onRestart }) => {
  const goodPct = result.percentage >= 70;

  const resultTitle = goodPct ? 'Отличный результат!' : 'Квиз завершён';
  const resultText = goodPct
    ? 'Вы хорошо справились. Ниже можно посмотреть ответы.'
    : 'Ниже можно посмотреть ошибки и правильные ответы.';

  const hintStyles = goodPct
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200'
    : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200';

  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white/90 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20">
      <div className={`relative overflow-hidden p-7 text-center text-white sm:p-10 ${goodPct ? 'bg-gradient-to-br from-emerald-500 to-teal-700' : 'bg-gradient-to-br from-amber-500 to-orange-700'}`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.22),transparent_30%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.18),transparent_24%)]" />
        <div className="relative z-10">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-[1.75rem] bg-white/20 backdrop-blur-xl">
            <Trophy className="h-11 w-11" />
          </div>
          <p className="text-sm font-semibold text-white/80">{participantName}, вы набрали</p>
          <h2 className="mt-1 text-6xl font-black tracking-tight sm:text-7xl">{result.percentage}%</h2>
          <p className="mt-2 text-sm font-semibold text-white/85 tabular-nums">
            {result.score} из {result.totalQuestions}
          </p>
          <h3 className="mt-5 text-2xl font-black">{resultTitle}</h3>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-white/80">{resultText}</p>

          <div
            className={`mx-auto mt-5 max-w-md rounded-2xl border px-4 py-3 text-sm font-semibold backdrop-blur-xl ${hintStyles}`}
          >
            {goodPct
              ? 'Вы уверенно прошли квиз и показали хороший результат.'
              : 'Посмотрите ошибки ниже — это поможет лучше закрепить материал.'}
          </div>
        </div>
      </div>

      <div className="p-5 sm:p-8">
        {Array.isArray(result.breakdown) && quiz?.questions && (
          <div className="space-y-3">
            {result.breakdown.map((item, i) => {
              const q = quiz.questions[i];
              return <ReviewItem key={String(item.questionId)} item={item} question={q} index={i} />;
            })}
          </div>
        )}

        <div className="mt-8 grid gap-3 rounded-3xl border border-indigo-100 bg-indigo-50 p-5 text-center dark:border-indigo-500/20 dark:bg-indigo-500/10 sm:p-6">
          <p className="text-sm font-semibold leading-6 text-indigo-950 dark:text-indigo-100">
            Хотите создавать свои квизы, собирать ответы и продавать курсы?
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            
            <Link
              to="/auth"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-indigo-500/20 transition hover:-translate-y-0.5 hover:bg-indigo-700"
            >
              <UserPlus className="h-4 w-4" />
              Зарегистрироваться на Uaqyt
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReviewItem = ({ item, question, index }) => {
  const isCorrect = item.isCorrect;

  return (
    <div
      className={`rounded-3xl border p-4 sm:p-5 ${
        isCorrect
          ? 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/20 dark:bg-emerald-500/10'
          : 'border-red-200 bg-red-50/70 dark:border-red-500/20 dark:bg-red-500/10'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl ${
            isCorrect
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
              : 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300'
          }`}
        >
          {isCorrect ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-sm font-black leading-6 text-slate-950 dark:text-white [&_p]:m-0">
            <span>{index + 1}. </span>
            <TheoryRenderer content={question?.text || ''} />
          </div>

          <div className="mt-3 space-y-2">
            {question?.options?.map((option, k) => {
              const optionCorrect = k === item.correctOptionIndex;
              const optionSelected = k === item.selectedOptionIndex;

              return (
                <div
                  key={option._id || k}
                  className={`flex items-start gap-2 rounded-2xl px-3 py-2 text-sm ${
                    optionCorrect
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200'
                      : optionSelected
                      ? 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-200'
                      : 'bg-white/70 text-slate-600 dark:bg-white/5 dark:text-slate-300'
                  }`}
                >
                  <span className="shrink-0 font-black">
                    {optionCorrect ? '✓' : optionSelected ? '✗' : '•'}
                  </span>
                  <div className={`min-w-0 flex-1 [&_p]:m-0 ${optionSelected && !optionCorrect ? 'line-through' : ''}`}>
                    <TheoryRenderer content={option.text || ''} />
                  </div>
                </div>
              );
            })}
          </div>

          {item.explanation && (
            <div className="mt-3 rounded-2xl bg-white/70 p-3 text-xs leading-5 text-slate-600 dark:bg-white/5 dark:text-slate-300 [&_p]:m-0">
              <TheoryRenderer content={item.explanation} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Badge = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-black text-indigo-700 shadow-sm backdrop-blur-xl dark:bg-slate-950/70 dark:text-indigo-300">
    <Icon className="h-3.5 w-3.5" />
    {children}
  </span>
);

const MiniInfo = ({ icon: Icon, title, text }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.03]">
    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-indigo-600 dark:bg-white/10 dark:text-indigo-300">
      <Icon className="h-5 w-5" />
    </div>
    <h3 className="text-sm font-black text-slate-950 dark:text-white">{title}</h3>
    <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{text}</p>
  </div>
);

export default PublicQuizPage;
